/* eslint-disable array-callback-return */
import ReactGA from 'react-ga4';
import { useEffect } from 'react';
// Import the functions you need from the SDKs you need
import { useLocation } from 'react-router';

import { AxiosHttpClient } from 'src/http';
import { useUserStore } from 'src/stores/user/user.store';
import { useAnalytics } from 'src/stores/analytics/analytics.store';
import { API_URL, FIREBASE_MEASUREMENT_ID } from 'src/config-global';
import { useCustomContent } from 'src/stores/custom-content/custom-content.store';
import { AnalyticsRepository } from 'src/repositories/analytics/analytics.repository';

const navEvents = [
  { path: 'home', pageName: 'LPEventsHome' },
  { path: 'benefits', pageName: 'LPEventsMyBenefits' },
  { path: 'profile', pageName: 'LPEventsUserProfile' },
  { path: 'login', pageName: 'LPEventsLogin' },
];

const saveActionsToLocalStorage = (actions: any) => {
  localStorage.setItem('events', JSON.stringify(actions));
};
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export default function useFirebase() {
  const { user } = useUserStore();

  const { addEvent, clearEvents, events } = useAnalytics((state) => state);

  const { businessUnity, eventData } = useCustomContent((state) => state);

  const api = new AxiosHttpClient(API_URL);
  const repository = new AnalyticsRepository(api);

  const location = useLocation();

  const loadGAScript = (id: string) => {
    if (document.querySelector(`script[src*="id=${id}"]`)) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(script);
  };

  const initGtag = () => {
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;
    gtag('js', new Date());
  };

  const initializeAnalytics = async () => {
    if (eventData && user?.cpf) {
      ReactGA.initialize([
        {
          trackingId: FIREBASE_MEASUREMENT_ID,
          gaOptions: {
            userId: btoa(user.cpf),
          },
        },
      ]);

      if (!window.gtag) initGtag();

      const firebaseId = FIREBASE_MEASUREMENT_ID!;
      loadGAScript(firebaseId);

      window.gtag('config', firebaseId, btoa(user?.cpf!) ? { user_id: btoa(user?.cpf!) } : {});

      if (eventData?.GAPropertyId) {
        const extraId = eventData.GAPropertyId;
        loadGAScript(extraId);
        window.gtag('config', extraId, btoa(user?.cpf!) ? { user_id: btoa(user?.cpf!) } : {});
      }
    }
  };

  const addNavigationEvent = async () => {
    navEvents.forEach((e) => {
      if (location.pathname.includes(e.path)) {
        const newEvent = {
          eventType: 'default_navigation',
          eventDate: new Date(),
          actionType: 'default_navigation',
          parameter: location.pathname,
          screenName: e.pageName,
          otherData: {
            screenName: e.pageName,
            bu: businessUnity?.name,
            lp_event: eventData?.name,
            bu_id: businessUnity?.id,
            event_id: eventData?._id,
          },
          GAPropertyId: FIREBASE_MEASUREMENT_ID,
          target: ['cn_firebase', 'roxcode'],
        };
        addEvent(newEvent);
        saveActionsToLocalStorage([...events, newEvent]);
      }
    });
  };

  const sendNavEvents = async () => {
    const getGA4Data = (
      measurementId: string
    ): {
      clientId: string | null;
      sessionId: string | null;
      sessionNumber: string | null;
      eventSequence: string | null;
    } => {
      let clientId: string | null = null;
      let sessionId: string | null = null;
      let sessionNumber: string | null = null;
      let eventSequence: string | null = null;

      const gaCookie = document.cookie.split('; ').find((row) => row.startsWith('_ga='));
      if (gaCookie) {
        const parts = gaCookie.split('.');
        clientId = parts.slice(-2).join('.');
      }

      const gaMeasurementCookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`_ga_${measurementId?.replace('G-', '')}=`));
      if (gaMeasurementCookie) {
        const value = gaMeasurementCookie.split('=')[1];

        const sessionIdMatch = value.match(/s(\d+)/);
        if (sessionIdMatch && sessionIdMatch[1]) {
          sessionId = sessionIdMatch[1];
        }

        const sessionNumberMatch = value.match(/\$o(\d+)\$g/);
        if (sessionNumberMatch && sessionNumberMatch[1]) {
          sessionNumber = sessionNumberMatch[1];
        }

        const eventSequenceMatch = value.match(/\$j(\d+)/);
        if (eventSequenceMatch && eventSequenceMatch[1]) {
          eventSequence = eventSequenceMatch[1];
        }
      }

      return { clientId, sessionId, sessionNumber, eventSequence };
    };

    const verifySession = getGA4Data(FIREBASE_MEASUREMENT_ID);

    if (
      user?.cpf &&
      verifySession.clientId &&
      verifySession.sessionId &&
      verifySession.sessionNumber
    ) {
      await repository.sendManyEvents(
        events.map((event) => {
          const { clientId, sessionId, sessionNumber, eventSequence } = getGA4Data(
            event.GAPropertyId!
          );
          return {
            ...event,
            otherData: {
              ...event.otherData,
              userId: btoa(user?.cpf!),
              clientId,
              sessionId,
              sessionNumber,
              eventSequence,
            },
            origin: 'LPEvents',
          };
        })
      );
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (events?.length > 0) {
        sendNavEvents();
        clearEvents();
        saveActionsToLocalStorage([]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [events, user]);

  useEffect(() => {
    if (eventData) {
      addNavigationEvent();
    }
  }, [location.pathname, eventData]);

  useEffect(() => {
    initializeAnalytics();
  }, [eventData, user]);

  return {
    initializeAnalytics,
    addEvent,
  };
}
